// EmployeeSalarySummary

import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { ReportPageShell } from "../../components/ui/reportPageShell";
import { SALARYSUMMARY_REPORTS } from "../../constants/api";
import { EmployeeDetails } from "../../components/ui/cards";

const EmployeeSalarySummary = () => {
    const [employeeSalarySummaryData, setEmployeeSalarySummaryData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    // Helper function to render object values safely
    const renderObjectValue = (value, keyToShow = "TOTAL") => {
        if (value === null || value === undefined) {
            return 0;
        }

        if (typeof value === 'object' && !Array.isArray(value)) {
            const result = value[keyToShow];
            return result !== null && result !== undefined ? result : 0;
        }

        return value ?? 0;
    };

    const employeeSalarySummaryColumns = [
        {
            accessor: "employee",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "300px",
            Cell: ({ row }) => {
                const emp = row?.employee;
                if (!emp || typeof emp !== "object") {
                    return <span>{typeof emp === "string" ? emp : "-"}</span>;
                }

                const empDetails = {
                    ...emp,
                    designation_id: emp.designation
                        ? {
                            id: emp.designation.id,
                            designation_name: emp.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: emp.employee_name || emp.first_name || "-",
                    employee_id: emp.employee_id || emp.id,
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "salary_for",
            Header: "Salary For",
            sortable: true,
            Cell: ({ value }) => value ?? "-",
        },
        {
            // Already a display label from the API (SALARY_TYPE_LABELS), so there
            // is nothing to map here. The CSV/PDF cell differs only in casing,
            // because tools.utils.format_string capitalizes every export value.
            accessor: "salary_type",
            Header: "Salary Type",
            sortable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "earnings",
            Header: "Earnings",
            Cell: ({ value }) => renderObjectValue(value, "TOTAL"), // Show TOTAL from earnings object
        },
        {
            accessor: "credits",
            Header: "Credit",
            filterable: true,
            Cell: ({ value }) => renderObjectValue(value, "TOTAL"), // Show TOTAL from credits object
        },
        {
            accessor: "deductions",
            Header: "Deduction",
            Cell: ({ value }) => renderObjectValue(value, "TOTAL"), // Show TOTAL from deductions object
        },
        {
            accessor: "debit",
            Header: "Debit",
            Cell: ({ value }) => renderObjectValue(value, "TOTAL"), // Show TOTAL from debit object
        },
        {
            accessor: "paid_salary",
            Header: "Take Home",
            sortable: true,
            Cell: ({ value }) => (value === null || value === undefined ? 0 : value),
        }
    ];

    return (
        <ReportPageShell title="Employee Salary Summary Report" count={totalRecords}>
            {totalRecords === 0 && (
                <p className="mb-3 text-sm text-amber-800 dark:text-amber-200">
                    Unpublished payroll periods stay empty until payslips are published. Zero totals print as 0, not a blank cell.
                </p>
            )}
            <Reactable
                columns={employeeSalarySummaryColumns}
                data={employeeSalarySummaryData}
                setData={setEmployeeSalarySummaryData}
                setTotalCount={setTotalRecords}
                url={SALARYSUMMARY_REPORTS}
                serverSide={true}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isMonthYearRequired={true}
            />
        </ReportPageShell>
    );
}

export default EmployeeSalarySummary;